import { CloseCode } from "odyssey-shared";
import type { EN } from "odyssey-lang";
import { say } from "../words.ts";

/**
 * The disconnect notices (P1.9-T15, the author's ruling of 2026-09-25): what the page tells a player whose
 * connection ended. A raw code means nothing to a player, so every close code the page can meet maps to a
 * notice: a title, one player sentence for the reason, the action its button offers, the code and the
 * server's reason as a small detail line for support, and whether that action reconnects in place or reloads
 * the page. After the world is shown, a close takes the player out of it to this notice in the loading
 * shell's full-screen failure layout; before the reveal, the shell's own failures take their sentences and
 * detail lines from the same table (`loading-failure.ts`), so a code reads the same on both sides. A sign-in
 * that ends while the page plays is no close, and uses the same notice (`signInNotice`).
 *
 * The action follows one rule: Reload where the page itself may be out of step with the server (a protocol
 * version, a message neither could read, a world version, a code this page does not know); Log in where the
 * sign-in failed or ended; Reconnect everywhere else, which joins again through the loading shell and reuses
 * the world already loaded. A page from before a deploy is refused at `hello` with 4000, and one that cannot
 * read what the server sends closes with 4002; joining again from it fails the same way, so both say the
 * game has been updated and offer Reload, before the reveal as after it (the P2.1-T1 review). Every notice
 * whose button is Reload says so, since its reason alone does not tell a player that a reload mends it.
 * Nothing reconnects by itself: a player chooses.
 *
 * The words are drafts for the author (ADR-061, amendment "the export is a visual reference"): sentence case,
 * a full stop after a sentence and none after a title or a button, and "server" the only word for the
 * machines behind the game (ADR-060). The sentences the message line showed before (a character joining from
 * somewhere else, a quiet connection, a sign-in that ended) keep their words in sentence case; the quiet one
 * leaves its "reload to play again" to the button. None is taken from the design kit.
 */

/**
 * What a notice's button does: join again through the loading shell, reusing what the page has loaded;
 * reload the page; forget the tab's sign-in and reload onto the log in form; or, for maintenance (ADR-078 decision
 * 6), do the same, which during maintenance opens on the maintenance page.
 */
export type NoticeAction = "reconnect" | "reload" | "log-in" | "status";

/**
 * A notice: its title, the player sentence, the action its button offers, the detail line for support, and
 * whether the action reloads the page (true for Reload and Log in) rather than reconnecting in place.
 */
export interface CloseNotice {
  readonly title: string;
  readonly sentence: string;
  readonly action: NoticeAction;
  readonly detail: string;
  readonly reloads: boolean;
}

/**
 * One row of the table: the sentence, the action, and a title of its own where the default would alarm a
 * player who brought the close about through their own account, or where the close is no fault. `atJoin` marks a
 * row whose title also stands before the reveal, in place of the loading shell's "Server refused" and the code: a
 * full server and a full queue, which a player meets only at a join and which are refusals of no fault.
 */
export interface CloseRow {
  readonly sentence: string;
  readonly action: NoticeAction;
  readonly title?: string;
  readonly atJoin?: true;
}

/**
 * One row of the table as it is held: the sentence and the title by their names in the language files
 * (`close.sentences.*`, `close.titles.*`), worded when a notice is made (`rowOf`).
 */
export interface CloseEntry {
  readonly sentence: keyof (typeof EN)["words"]["close"]["sentences"];
  readonly action: NoticeAction;
  readonly title?: keyof (typeof EN)["words"]["close"]["titles"];
  readonly atJoin?: true;
  readonly retries?: true;
}

/**
 * A row of the table in words.
 *
 * @param entry The row as held.
 * @returns The row, its sentence and its title worded.
 */
export function rowOf(entry: CloseEntry): CloseRow {
  return { sentence: say(`close.sentences.${entry.sentence}`), action: entry.action, ...(entry.title === undefined ? {} : { title: say(`close.titles.${entry.title}`) }), ...(entry.atJoin === undefined ? {} : { atJoin: true as const }) };
}

/**
 * The words on a notice's button.
 *
 * @param action What the button does.
 * @returns Its words, "Reconnect".
 */
export function actionWords(action: NoticeAction): string {
  return action === "log-in" ? say("close.actions.logIn") : say(`close.actions.${action}`);
}

/**
 * Why a sign-in ended while the page played: the page could no longer renew it, or the gateway answered that
 * the account needs a name first.
 */
export type SignInEnd = "expired" | "naming";

/**
 * A socket that closed with no close frame from the server: none was given (1005), the connection was lost
 * or never made (1006), or its TLS handshake failed (1015).
 */
const LOST: CloseEntry = { sentence: "lost", action: "reconnect", retries: true };

/**
 * The table: every close code the page can meet (PROTOCOL.md section 8, and the socket's own 1000, 1001,
 * 1005, 1006 and 1015) to its row. 4001 is an account's refused sign-in here; a `?token=` page's is
 * `REFUSED_TOKEN`. 4008, a close by a GM, shows what the gateway told just before it, the kick's or the ban's reason
 * (`closeNotice`'s `told`, ADR-056's amendment of 2026-10-04), and this row when nothing was told. 4011, maintenance, offers View
 * status, which forgets the tab's sign-in and reloads onto the maintenance page (ADR-078 decision 6). 4012, a
 * character of the account still lingering in a fight, offers Reconnect, which joins once the linger has ended
 * (ADR-084 decision 14). 4013, a `hello` refused because the login queue is full, offers Reconnect, which tries again
 * (ADR-127 decisions 5 and 6); its title and its sentence are drafts, in the queue window's word for the line ("The
 * queue is full", ADR-127's "told to try later"). 4004 is a join the map server could not place, except with the reason `server full`,
 * which is `SERVER_FULL`.
 *
 * `retries` marks the closes a later try can cure, which the page tries again in the background before it shows the
 * notice (P1.9-T32, the author's ruling of 2026-09-25): a dropped connection (1005, 1006, 1015), a quiet one (4010)
 * and a server shutting down (1001, 4005). Every other row goes to the notice at once: a close that meets the same
 * wall again (4000, 4001, 4002, 4007, 4008), a tab another took the character from (4009, which two tabs would trade
 * back and forth), and a close that tells the player to wait or that is a normal one (1000, 4003, 4004, 4006, 4011 to
 * 4013), whose retry the player chooses.
 */
export const CLOSE_TABLE: ReadonlyMap<number, CloseEntry> = new Map<number, CloseEntry>([
  [1000, { sentence: "closed", action: "reconnect" }],
  [1001, { sentence: "shuttingDown", action: "reconnect", retries: true }],
  [1005, LOST],
  [1006, LOST],
  [1015, LOST],
  [CloseCode.VersionMismatch, { sentence: "gameUpdated", action: "reload" }],
  [CloseCode.AuthFailed, { sentence: "loggedOut", action: "log-in", title: "loggedOut" }],
  [CloseCode.Malformed, { sentence: "gameUpdated", action: "reload" }],
  [CloseCode.RateLimited, { sentence: "tooFast", action: "reconnect" }],
  [CloseCode.HandoffFailed, { sentence: "notPlaced", action: "reconnect" }],
  [CloseCode.ShuttingDown, { sentence: "shuttingDown", action: "reconnect", retries: true }],
  [CloseCode.ChannelFull, { sentence: "tooSlow", action: "reconnect" }],
  [CloseCode.WorldUpdated, { sentence: "worldUpdated", action: "reload" }],
  [CloseCode.Moderated, { sentence: "removed", action: "reconnect", title: "removed" }],
  [CloseCode.SessionReplaced, { sentence: "elsewhere", action: "reconnect", title: "elsewhere" }],
  [CloseCode.Quiet, { sentence: "quiet", action: "reconnect", retries: true }],
  [CloseCode.Maintenance, { sentence: "maintenance", action: "status", title: "maintenance" }],
  [CloseCode.CharacterLingering, { sentence: "lingering", action: "reconnect", title: "lingering" }],
  [CloseCode.QueueFull, { sentence: "queueFull", action: "reconnect", title: "queueFull", atJoin: true }],
]);

/**
 * Whether a close is one the page tries again in the background before it shows the notice (P1.9-T32).
 *
 * @param code The close code.
 * @returns True for a dropped connection, a quiet one and a server shutting down; false for every other code, one the
 *   table does not hold included.
 * @example
 * reconnects(1006);
 * // true
 */
export function reconnects(code: number): boolean {
  return CLOSE_TABLE.get(code)?.retries === true;
}

/**
 * A `?token=` page's refused sign-in (4001): it has no log in form, so it connects again with its token.
 */
export const REFUSED_TOKEN: CloseEntry = { sentence: "refusedToken", action: "reconnect" };

/**
 * The reason the gateway gives a join it refused because the world has no place for it (4004, P1.4-T23), in its
 * `error` message and on its close frame.
 */
export const SERVER_FULL_REASON = "server full";

/**
 * A join refused because the server is full (4004 with `SERVER_FULL_REASON`; ADR-127 question 6, the author's
 * answer of 2026-10-12): the sentence says so and that trying again helps, where 4004's own sentence says neither.
 * With the login queue a page rarely meets it: the table's backstop, and a gateway that runs no cap.
 */
export const SERVER_FULL: CloseEntry = { sentence: "serverFull", action: "reconnect", title: "serverFull", atJoin: true };

/**
 * A 4xxx code the table does not hold: the server speaks a code this page does not know, so the page is older
 * than the server and a reload brings the page the server expects.
 */
const UNKNOWN_APPLICATION_CODE: CloseEntry = { sentence: "gameUpdated", action: "reload" };

/**
 * Any other code the table does not hold: the server closing the connection.
 */
const OTHER: CloseEntry = { sentence: "closed", action: "reconnect" };

/**
 * The detail line for a close: the code and the server's own reason (PROTOCOL.md section 8), for support.
 *
 * @param code The close code.
 * @param reason The server's reason, which may be empty.
 * @returns `closed: <code> <reason>`, or `closed: <code>` with no reason.
 * @example
 * closedDetail(4009, "session replaced");
 * // "closed: 4009 session replaced"
 */
export function closedDetail(code: number, reason: string): string {
  const said = reason.trim();
  return said === "" ? say("close.details.closed", { code: String(code) }) : say("close.details.closedSaid", { code: String(code), reason: said });
}

/**
 * The row for a close code.
 *
 * @param code The close code, from the server's `error` message or the socket's close.
 * @param account Whether the page plays an account session, whose refused sign-in means logging in again.
 * @param reason The server's reason, which tells a full server from 4004's other refusals.
 * @returns The row.
 */
function closeRow(code: number, account: boolean, reason: string): CloseRow {
  if (code === CloseCode.AuthFailed && !account) {
    return rowOf(REFUSED_TOKEN);
  }
  if (code === CloseCode.HandoffFailed && reason.trim() === SERVER_FULL_REASON) {
    return rowOf(SERVER_FULL);
  }
  return rowOf(CLOSE_TABLE.get(code) ?? (code >= 4000 && code <= 4999 ? UNKNOWN_APPLICATION_CODE : OTHER));
}

/**
 * A notice from a row.
 *
 * @param row The row.
 * @param detail The detail line.
 * @returns The notice, under the default title unless the row has its own.
 */
function noticeOf(row: CloseRow, detail: string): CloseNotice {
  return { title: row.title ?? say("close.titles.disconnected"), sentence: row.sentence, action: row.action, detail, reloads: row.action !== "reconnect" };
}

/**
 * The notice for a close after the world is shown.
 *
 * @param code The close code, from the server's `error` message or the socket's close.
 * @param reason The server's reason, which may be empty.
 * @param account Whether the page plays an account session rather than a `?token=` one.
 * @param told For a close by a GM (4008), the row the gateway's last `moderation` message makes, with the kick's
 *   or the ban's reason; null when nothing was told, and ignored for every other code.
 * @returns The notice.
 * @example
 * closeNotice(1006, "", true);
 * // { title: "You have been disconnected", sentence: "The connection to the server was lost.",
 * //   action: "reconnect", detail: "closed: 1006", reloads: false }
 */
export function closeNotice(code: number, reason: string, account: boolean, told: CloseRow | null = null): CloseNotice {
  return noticeOf(code === CloseCode.Moderated && told !== null ? told : closeRow(code, account, reason), closedDetail(code, reason));
}

/**
 * The title a close keeps before the reveal, where the loading shell otherwise titles a server's close "Server
 * refused" with its code (`loading-failure.ts`): a full server's and a full queue's, which are no fault and are met
 * only at a join (ADR-127 question 6, P1.4-T164). Every other close has none there, whatever title its notice has
 * after the reveal.
 *
 * @param code The close code.
 * @param reason The server's reason, which may be empty.
 * @param account Whether the page plays an account session rather than a `?token=` one.
 * @returns The title, or null when the shell's own stands.
 * @example
 * joinTitle(4004, "server full", true);
 * // "The server is full"
 */
export function joinTitle(code: number, reason: string, account: boolean): string | null {
  const row = closeRow(code, account, reason);
  return row.atJoin === true ? (row.title ?? null) : null;
}

/**
 * The notice for a sign-in that ended while the page played, which is no close: the page could no longer
 * renew it (the account was logged out everywhere, or the sign-in expired), which asks the player to log in
 * again; or the gateway answered that the account needs a name first, which the name step asks for after a
 * reload.
 *
 * @param end Why the sign-in ended.
 * @returns The notice, its detail line naming why for support.
 */
export function signInNotice(end: SignInEnd): CloseNotice {
  const row: CloseRow = end === "expired" ? { sentence: say("close.sentences.loggedOut"), action: "log-in", title: say("close.titles.loggedOut") } : { sentence: say("close.sentences.naming"), action: "reload", title: say("close.titles.naming") };
  return noticeOf(row, say(`close.details.${end}`));
}
